import React, { useState } from 'react';
import './RegistrationPage.css';
import { getErrorMessage } from '../utils/api';

interface ParentRegistrationPageProps {
  onBackToLogin: () => void;
  onRegistrationSuccess: () => void;
}

const ParentRegistrationPage: React.FC<ParentRegistrationPageProps> = ({ onBackToLogin, onRegistrationSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    whatsapp_phone: '',
    address: '',
    number_of_children: 1,
    relationship_type: 'Parent',
  });
  const [playerNames, setPlayerNames] = useState<string[]>(['']);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    if (name === 'last_name') {
      setFormData({ ...formData, [name]: value.toUpperCase() });
    } else if (name === 'number_of_children') {
      const num = parseInt(value) || 1;
      setFormData({ ...formData, [name]: num });
      // Adjust player names array
      const newPlayerNames = [...playerNames];
      while (newPlayerNames.length < num) {
        newPlayerNames.push('');
      }
      while (newPlayerNames.length > num) {
        newPlayerNames.pop();
      }
      setPlayerNames(newPlayerNames);
      // Clear player name errors when number changes
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith('player_name_')) delete newErrors[key];
        });
        return newErrors;
      });
    } else if (name.startsWith('player_name_')) {
      const index = parseInt(name.split('_')[2]);
      const newPlayerNames = [...playerNames];
      newPlayerNames[index] = value;
      setPlayerNames(newPlayerNames);
      // Clear error for this specific player name
      if (fieldErrors[name]) {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Required fields based on backend validation
    if (!formData.username.trim()) errors.username = 'Username is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.password) errors.password = 'Password is required';
    if (!formData.first_name.trim()) errors.first_name = 'First name is required';
    if (!formData.last_name.trim()) errors.last_name = 'Last name is required';
    if (!formData.whatsapp_phone.trim()) errors.whatsapp_phone = 'WhatsApp phone is required';
    if (!formData.address.trim()) errors.address = 'Address is required';
    
    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Password validation
    if (formData.password) {
      if (formData.password.length < 8) {
        errors.password = 'Password must be at least 8 characters long';
      } else if (!/[a-zA-Z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
        errors.password = 'Password must contain both letters and numbers';
      }
    }
    
    // Validate player names
    const validPlayerNames = playerNames.filter(name => name.trim() !== '');
    if (validPlayerNames.length === 0) {
      errors.player_names = 'Please enter at least one player name';
    } else {
      // Check each player name
      playerNames.forEach((name, index) => {
        if (!name.trim()) {
          errors[`player_name_${index}`] = 'Player name is required';
        }
      });
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    
    // Validate form before submission
    if (!validateForm()) {
      setError('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      const validPlayerNames = playerNames.filter(name => name.trim() !== '');
      const { buildApiUrl } = require('../config/api');
      const response = await fetch(buildApiUrl('/auth/register/parent'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name.toUpperCase(),
          whatsapp_phone: formData.whatsapp_phone,
          physical_address: formData.address, // Backend expects physical_address, not address
          number_of_children: formData.number_of_children,
          player_names: validPlayerNames,
          relationship_type: formData.relationship_type,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        alert(`Registration successful! Your credentials have been sent to your WhatsApp number: ${formData.whatsapp_phone}\n\nUsername: ${data.username}`);
        onRegistrationSuccess();
      } else {
        setError(getErrorMessage(data, 'Registration failed'));
      }
    } catch (err) {
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="registration-page">
      <div className="registration-container">
        <h2>Parent Registration</h2>
        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
          {/* Hidden dummy fields to prevent browser autocomplete */}
          <input type="text" style={{ position: 'absolute', left: '-9999px', opacity: 0 }} autoComplete="off" tabIndex={-1} readOnly />
          <input type="password" style={{ position: 'absolute', left: '-9999px', opacity: 0 }} autoComplete="off" tabIndex={-1} readOnly />
          <input type="email" style={{ position: 'absolute', left: '-9999px', opacity: 0 }} autoComplete="off" tabIndex={-1} readOnly />
          <div className="form-row">
            <div className="form-group">
              <label>Username *</label>
              <input 
                type="text" 
                name="username" 
                value={formData.username} 
                onChange={handleChange} 
                required 
                autoComplete="off" 
                id="parent-username"
                className={fieldErrors.username ? 'field-error' : ''}
              />
              {fieldErrors.username && <span className="field-error-message">{fieldErrors.username}</span>}
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input 
                type="email" 
                name="email" 
                value={formData.email} 
                onChange={handleChange} 
                required 
                autoComplete="off"
                className={fieldErrors.email ? 'field-error' : ''}
              />
              {fieldErrors.email && <span className="field-error-message">{fieldErrors.email}</span>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input 
                type="text" 
                name="first_name" 
                value={formData.first_name} 
                onChange={handleChange} 
                required 
                autoComplete="off"
                className={fieldErrors.first_name ? 'field-error' : ''}
              />
              {fieldErrors.first_name && <span className="field-error-message">{fieldErrors.first_name}</span>}
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input 
                type="text" 
                name="last_name" 
                value={formData.last_name} 
                onChange={handleChange} 
                required 
                autoComplete="off"
                className={fieldErrors.last_name ? 'field-error' : ''}
              />
              {fieldErrors.last_name && <span className="field-error-message">{fieldErrors.last_name}</span>}
            </div>
          </div>
          <div className="form-group">
            <label>Password *</label>
            <input 
              type="password" 
              name="password" 
              value={formData.password} 
              onChange={handleChange} 
              required 
              autoComplete="new-password" 
              id="parent-password"
              data-lpignore="true"
              data-form-type="other"
              className={fieldErrors.password ? 'field-error' : ''}
            />
            {fieldErrors.password && <span className="field-error-message">{fieldErrors.password}</span>}
          </div>
          <div className="form-group">
            <label>WhatsApp Phone *</label>
            <input 
              type="tel" 
              name="whatsapp_phone" 
              value={formData.whatsapp_phone} 
              onChange={handleChange} 
              required 
              autoComplete="off"
              className={fieldErrors.whatsapp_phone ? 'field-error' : ''}
            />
            {fieldErrors.whatsapp_phone && <span className="field-error-message">{fieldErrors.whatsapp_phone}</span>}
          </div>
          <div className="form-group">
            <label>Address *</label>
            <input 
              type="text" 
              name="address" 
              value={formData.address} 
              onChange={handleChange} 
              required 
              autoComplete="off"
              className={fieldErrors.address ? 'field-error' : ''}
            />
            {fieldErrors.address && <span className="field-error-message">{fieldErrors.address}</span>}
          </div>
          <div className="form-group">
            <label>Number of Children Playing at Shosholoza *</label>
            <select name="number_of_children" value={formData.number_of_children} onChange={handleChange} required>
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </div>
          <div className="form-group">
            <label>Relationship to Player(s) *</label>
            <select name="relationship_type" value={formData.relationship_type} onChange={handleChange} required>
              <option value="Parent">Parent</option>
              <option value="Father">Father</option>
              <option value="Mother">Mother</option>
              <option value="Guardian">Guardian</option>
            </select>
          </div>
          {playerNames.map((playerName, index) => (
            <div key={index} className="form-group">
              <label>
                Name of Player {index + 1} * {index === 0 && <span style={{ fontSize: '0.85rem', color: '#567ad8' }}>(Links to Player's Dashboard)</span>}
              </label>
              <input
                type="text"
                name={`player_name_${index}`}
                value={playerName}
                onChange={handleChange}
                required
                autoComplete="off"
                placeholder="Enter full name (e.g., John Smith)"
                className={fieldErrors[`player_name_${index}`] || fieldErrors.player_names ? 'field-error' : ''}
              />
              {(fieldErrors[`player_name_${index}`] || (fieldErrors.player_names && index === 0)) && (
                <span className="field-error-message">
                  {fieldErrors[`player_name_${index}`] || fieldErrors.player_names}
                </span>
              )}
            </div>
          ))}
          {error && <div className="error-message">{error}</div>}
          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>
        <button onClick={onBackToLogin} className="back-btn">Back to Login</button>
      </div>
    </div>
  );
};

export default ParentRegistrationPage;


